import DataApiClient from 'src/utils/DataApiClient';
import dayjs from 'dayjs';
import { JsonTrade } from '@velocity-exchange/common';
import {
	syncGetCurrentSettings,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { getSettingIsPristine } from 'src/providers/settingsProvider';

interface AppContextData {
	currentMarketIndex: number;
	currentAuthorityAddress: string | null;
	currentSubAccountAddress: string | null;
	shouldShowMarks: boolean;
}

interface DateRange {
	start: number;
	end: number;
}

interface TimeRange {
	start: number;
	end: number;
}

interface SerializableCacheEntry {
	data: Record<string, JsonTrade>;
	coveredTimeRanges: TimeRange[];
}

interface CacheEntry {
	data: Map<string, JsonTrade>;
	coveredTimeRanges: TimeRange[];
}

interface CacheConfig {
	maxEntries: number;
	storageKey: string;
}

const TRADE_MARK_THRESHOLD = 100;

export const TV_TRADES_CACHE_STORAGE_KEY = 'tv_trades_cache_v1';

export class TvAppTradeDataManager {
	private currentMarketIndex: number;
	private currentAuthorityAddress: string | null;
	private currentSubAccountAddress: string | null;
	private shouldShowMarks: boolean;
	private updateSettings: ((newSettings: Partial<UserSettings>) => void) | null;

	// Simple cache system
	private cache: Map<string, CacheEntry>;
	private config: CacheConfig;

	constructor(updateSettings?: (newSettings: Partial<UserSettings>) => void) {
		this.currentMarketIndex = 0;
		this.currentAuthorityAddress = null;
		this.currentSubAccountAddress = null;
		this.shouldShowMarks = false;
		this.updateSettings = updateSettings || null;

		this.config = {
			// arbitrary large number to avoid memory issues
			maxEntries: 1000,
			storageKey: TV_TRADES_CACHE_STORAGE_KEY,
		};

		this.cache = new Map();

		this.loadCacheFromStorage();
	}

	private loadCacheFromStorage(): void {
		try {
			if (typeof window === 'undefined') return;

			const stored = localStorage.getItem(this.config.storageKey);
			if (!stored) return;

			const parsedCache: Record<string, SerializableCacheEntry> =
				JSON.parse(stored);

			Object.entries(parsedCache).forEach(([key, serializableEntry]) => {
				const entry: CacheEntry = {
					data: new Map(Object.entries(serializableEntry.data)),
					coveredTimeRanges: serializableEntry.coveredTimeRanges,
				};

				this.cache.set(key, entry);
			});

			console.log(`Loaded ${this.cache.size} cache entries from localStorage`);
		} catch (error) {
			console.warn('Failed to load cache from localStorage:', error);

			this.clearLocalStorage();
		}
	}

	private saveCacheToStorage(): void {
		try {
			if (typeof window === 'undefined') return;

			const serializableCache: Record<string, SerializableCacheEntry> = {};

			this.cache.forEach((entry, key) => {
				serializableCache[key] = {
					data: Object.fromEntries(entry.data),
					coveredTimeRanges: entry.coveredTimeRanges,
				};
			});

			localStorage.setItem(
				this.config.storageKey,
				JSON.stringify(serializableCache)
			);
		} catch (error) {
			console.warn('Failed to save cache to localStorage:', error);
		}
	}

	private clearLocalStorage(): void {
		try {
			if (typeof window !== 'undefined') {
				localStorage.removeItem(this.config.storageKey);
			}
		} catch (error) {
			console.warn('Failed to clear localStorage:', error);
		}
	}

	private checkCacheSize(): void {
		if (this.cache.size > this.config.maxEntries) {
			console.log(
				`Cache size (${this.cache.size}) exceeded limit (${this.config.maxEntries}), clearing all cache`
			);
			this.cache.clear();
			this.clearLocalStorage();
		}
	}

	// Simple range coverage check
	private isRangeCovered(
		requestedRange: DateRange,
		coveredRanges: TimeRange[]
	): boolean {
		const isRangeCovered = coveredRanges.some(
			(covered) =>
				covered.start <= requestedRange.start &&
				covered.end >= requestedRange.end
		);

		return isRangeCovered;
	}

	// Add this new helper method for merging overlapping ranges
	private mergeTimeRanges(
		existingRanges: TimeRange[],
		newRange: TimeRange
	): TimeRange[] {
		// Add the new range to existing ranges
		const allRanges = [...existingRanges, newRange];

		// Sort ranges by start time
		allRanges.sort((a, b) => a.start - b.start);

		const merged: TimeRange[] = [];

		for (const range of allRanges) {
			// If merged is empty or current range doesn't overlap with the last merged range
			if (merged.length === 0 || merged[merged.length - 1].end < range.start) {
				merged.push(range);
			} else {
				// Merge overlapping ranges by extending the end time
				merged[merged.length - 1].end = Math.max(
					merged[merged.length - 1].end,
					range.end
				);
			}
		}

		return merged;
	}

	private mergeTradesIntoCache(
		entry: CacheEntry,
		newTrades: JsonTrade[]
	): void {
		newTrades.forEach((trade) => {
			const tradeKey = trade.txSig;
			entry.data.set(tradeKey, trade);
		});

		this.saveCacheToStorage();
	}

	private filterDataByRange(
		data: JsonTrade[],
		startDateSeconds?: number,
		endDateSeconds?: number
	): JsonTrade[] {
		return data.filter((trade) => {
			if (startDateSeconds && trade.ts < startDateSeconds) return false;
			if (endDateSeconds && trade.ts > endDateSeconds) return false;
			return true;
		});
	}

	private getOrCreateCacheEntry(cacheKey: string): CacheEntry {
		let entry = this.cache.get(cacheKey);
		if (!entry) {
			entry = {
				data: new Map(),
				coveredTimeRanges: [],
			};
			this.cache.set(cacheKey, entry);
		}
		return entry;
	}

	private async fetchAndCacheData(
		range: DateRange,
		cacheKey: string
	): Promise<JsonTrade[]> {
		try {
			if (!this.currentSubAccountAddress || !this.currentAuthorityAddress) {
				return [];
			}

			const startDateDayjs = dayjs(range.start);
			const endDateDayjs = dayjs(range.end);

			const res = await DataApiClient.fetchLongTermData<JsonTrade>('trades', {
				userAccount: this.currentSubAccountAddress,
				authority: this.currentAuthorityAddress,
				startDate: startDateDayjs,
				endDate: endDateDayjs,
			});

			// Store all market data in cache for maximum reuse
			const allTradesData = res.records;

			// Update cache
			const entry = this.getOrCreateCacheEntry(cacheKey);
			this.mergeTradesIntoCache(entry, allTradesData);

			entry.coveredTimeRanges = this.mergeTimeRanges(entry.coveredTimeRanges, {
				start: range.start,
				end: range.end,
			});

			return allTradesData;
		} catch (error) {
			console.error('Failed to fetch data:', error);
			throw error;
		}
	}

	private autoDisableMarksIfNeeded(filteredTrades: JsonTrade[]): boolean {
		const currentSettings = syncGetCurrentSettings();

		if (
			this.updateSettings &&
			// Only auto-disable if user hasn't manually configured the setting.
			getSettingIsPristine('showBuysAndSellsOnChart')
		) {
			if (filteredTrades.length > TRADE_MARK_THRESHOLD) {
				if (currentSettings?.showBuysAndSellsOnChart) {
					this.updateSettings({
						showBuysAndSellsOnChart: false,
					});
				}
				this.shouldShowMarks = false;
				return true; // Indicates marks were auto-disabled
			} else {
				if (!currentSettings?.showBuysAndSellsOnChart) {
					this.updateSettings({
						showBuysAndSellsOnChart: true,
					});
				}
				this.shouldShowMarks = true;
				return false;
			}
		}
		return false; // Marks were not auto-disabled
	}

	// Cache management methods
	clearCache(): void {
		this.cache.clear();
		this.clearLocalStorage();
	}

	// State management methods
	updateMarketIndex(marketIndex: number): void {
		this.currentMarketIndex = marketIndex;
	}

	updateAuthorityAddress(authorityAddress: string | null): void {
		this.currentAuthorityAddress = authorityAddress;
	}

	updateSubAccount(subAccountAddress: string | null): void {
		this.currentSubAccountAddress = subAccountAddress;
	}

	updateShouldShowMarks(shouldShowMarks: boolean): void {
		this.shouldShowMarks = shouldShowMarks;
	}

	// Getter methods
	getCurrentData(): AppContextData {
		return {
			currentMarketIndex: this.currentMarketIndex,
			currentAuthorityAddress: this.currentAuthorityAddress,
			currentSubAccountAddress: this.currentSubAccountAddress,
			shouldShowMarks: this.shouldShowMarks,
		};
	}

	getCurrentMarket(): number {
		return this.currentMarketIndex;
	}

	getCurrentWalletAddress(): string | null {
		return this.currentAuthorityAddress;
	}

	getCurrentSubAccountAddress(): string | null {
		return this.currentSubAccountAddress;
	}

	async getFilledOrdersData(
		startDateSeconds: number,
		endDateSeconds: number
	): Promise<JsonTrade[]> {
		if (!this.shouldShowMarks) {
			return [];
		}

		if (!this.currentSubAccountAddress || !this.currentAuthorityAddress) {
			return [];
		}

		const cacheKey = this.currentSubAccountAddress;

		this.checkCacheSize();
		const now = Date.now();

		const actualStartTimeMillis = Math.min(startDateSeconds * 1000, now);
		const actualEndTimeMillis = Math.min(endDateSeconds * 1000, now);

		const entry = this.cache.get(cacheKey);
		if (
			entry &&
			this.isRangeCovered(
				{ start: actualStartTimeMillis, end: actualEndTimeMillis },
				entry.coveredTimeRanges
			)
		) {
			const cachedData = Array.from(entry.data.values());
			const filteredTrades = this.filterDataByRange(
				cachedData,
				startDateSeconds,
				endDateSeconds
			).filter((trade) => trade.marketIndex === this.currentMarketIndex);

			// Check if we should auto-disable marks based on trade count
			const wasAutoDisabled = this.autoDisableMarksIfNeeded(filteredTrades);

			// If auto-disabled, return empty array instead of trades
			if (wasAutoDisabled) {
				return [];
			}

			return filteredTrades;
		}

		try {
			const result = await this.fetchAndCacheData(
				{ start: actualStartTimeMillis, end: actualEndTimeMillis },
				cacheKey
			);

			const filteredTrades = this.filterDataByRange(
				result,
				startDateSeconds,
				endDateSeconds
			).filter((trade) => trade.marketIndex === this.currentMarketIndex);

			// Check if we should auto-disable marks based on trade count
			const wasAutoDisabled = this.autoDisableMarksIfNeeded(filteredTrades);

			// If auto-disabled, return empty array instead of trades
			if (wasAutoDisabled) {
				return [];
			}

			return filteredTrades;
		} catch (error) {
			console.error('Error fetching data:', error);
			return [];
		}
	}
}
